import { useLayoutEffect, useRef } from 'react';
import { CATEGORIES, TYPE_COLORS_BG, TYPE_COLORS_FG } from '../constants';
import { S } from '../strings';
import type { Category, ParsedItem, UpdateType } from '../types';

type CategoryFilter = Category | 'Kaikki';

export interface DashboardFiltersProps {
  data: ParsedItem[];
  bookmarkCount: number;
  selectedCategory: CategoryFilter;
  selectedSubcategory: string | null;
  selectedType: UpdateType | null;
  showBookmarksOnly: boolean;
  onSelectCategory: (cat: CategoryFilter) => void;
  onSelectType: (type: UpdateType | null) => void;
  onToggleBookmarksOnly: () => void;
}

// Controlled filter row: bookmarks-only toggle, category pills, type pills.
// Filter state still lives in the shell so that view components (kanban /
// single-category / priority / roadmap) can read the same derived filtered
// list without prop-drilling through a second layer. This component only
// decides which setters to call when a pill is clicked.
//
// The scroll-into-view layout effect lives here because it guards the
// element this component renders (`#category-filters`) and fires on
// category/subcategory changes. Keeping the effect next to its DOM target
// avoids a cross-component implicit contract.
export function DashboardFilters({
  data,
  bookmarkCount,
  selectedCategory,
  selectedSubcategory,
  selectedType,
  showBookmarksOnly,
  onSelectCategory,
  onSelectType,
  onToggleBookmarksOnly,
}: DashboardFiltersProps) {
  // Avoid scroll jumping when filters change while the filter bar is off-screen.
  const isFirstRender = useRef(true);
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const el = document.getElementById('category-filters');
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight) {
        el.scrollIntoView({ block: 'start' });
      }
    }
  }, [selectedCategory, selectedSubcategory]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14,
          paddingBottom: 12,
          borderBottom: '1px dashed var(--border)',
        }}
      >
        <button
          onClick={onToggleBookmarksOnly}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            borderRadius: 22,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            border: '2px solid #B8881F',
            background: showBookmarksOnly ? '#B8881F' : 'transparent',
            color: showBookmarksOnly ? '#fff' : '#B8881F',
            transition: 'all 0.15s',
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>{showBookmarksOnly ? '★' : '☆'}</span>
          <span>{S.bookmark.filter.replace('⭐ ', '')}</span>
          <span style={{ opacity: 0.75 }}>{bookmarkCount}</span>
        </button>
        {showBookmarksOnly && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Näytetään vain kirjanmerkityt päivitykset
          </span>
        )}
      </div>

      <div
        id="category-filters"
        style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}
      >
        {CATEGORIES.map((cat) => {
          const count =
            cat.value === 'Kaikki'
              ? data.length
              : data.filter((i) => i.category === cat.value).length;
          const active = selectedCategory === cat.value;
          return (
            <button
              key={cat.value}
              className="pill"
              onClick={() => onSelectCategory(cat.value)}
              style={{
                padding: '7px 18px',
                borderRadius: 22,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: active ? cat.color : 'var(--border)',
                background: active ? cat.color : 'var(--card-bg)',
                color: active ? '#fff' : 'var(--text-secondary)',
                boxShadow: active ? `0 3px 12px ${cat.color}33` : 'none',
              }}
            >
              {cat.label}
              <span style={{ marginLeft: 6, opacity: 0.75 }}>{count}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 22, flexWrap: 'wrap' }}>
        {(
          [
            { value: 'ga', label: S.typePills.launched },
            { value: 'preview', label: S.typePills.inPreview },
            { value: 'development', label: S.typePills.inDevelopment },
            { value: 'rollout', label: S.typePills.rollingOut },
            { value: 'deprecation', label: S.typePills.retirement },
            { value: 'announcement', label: S.typePills.announcement },
            { value: 'update', label: S.typePills.update },
          ] as { value: UpdateType; label: string }[]
        ).map((t) => {
          const baseItems =
            selectedCategory !== 'Kaikki'
              ? data.filter((i) => i.category === selectedCategory)
              : data;
          const count = baseItems.filter((i) => i.updateType === t.value).length;
          if (count === 0) return null;
          const active = selectedType === t.value;
          return (
            <button
              key={t.value}
              className="pill"
              onClick={() => onSelectType(active ? null : t.value)}
              style={{
                padding: '5px 12px',
                borderRadius: 16,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1.5px solid',
                transition: 'all 0.15s',
                borderColor: active ? TYPE_COLORS_FG[t.value] : 'var(--border)',
                background: active ? TYPE_COLORS_BG[t.value] : 'var(--card-bg)',
                color: active ? TYPE_COLORS_FG[t.value] : 'var(--text-muted)',
              }}
            >
              {t.label} {count}
            </button>
          );
        })}
      </div>
    </>
  );
}
